import React from 'react';
import { Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Mail, MessageCircle, X, Send, Sun, Moon, Phone, Menu, User, ArrowUp } from 'lucide-react';
import { useContact } from '../context/ContactContext';
import { useTheme } from '../context/ThemeContext';
import { useLocation, useNavigate } from 'react-router-dom';
import MouseSpotlight from '../components/ui/MouseSpotlight';
import Logo from '../components/ui/Logo';

export default function MainLayout() {
  const { isModalOpen, toggleModal, closeModal } = useContact();
  const { theme, toggleTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showBackToTop, setShowBackToTop] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = (e, href) => {
    e.preventDefault();
    const id = href.replace('#', '');
    
    if (location.pathname !== '/') {
      navigate('/#' + id);
      // Let the Home component handle scrolling on mount or via effect
    } else {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80; // Header height
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    setIsMenuOpen(false);
  };

  const menuLinks = [
    { name: 'Fiscal', href: '#fiscal' },
    { name: 'Contábil', href: '#contabil' },
    { name: 'Pessoal', href: '#pessoal' },
    { name: 'Legalização', href: '#legal' },
    { name: 'Certificado', href: '#certificado' },
    { name: 'eSocial', href: '#esocial' },
    { name: 'Seguros', href: '#seguros' },
    { name: 'IRPF', href: '#irpf' },
  ];

  return (
    <div className="bg-background text-foreground selection:bg-orange-500/30 font-inter transition-colors duration-300">
      <MouseSpotlight />
      {/* Header Glass Sticky */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-md bg-background/80 border-b border-foreground/5 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <a href="/" className="flex items-center gap-2">
              <Logo />
            </a>
          </div>
          
          <nav className="hidden xl:flex items-center gap-6">
            {menuLinks.map((link) => (
              <a 
                key={link.name}
                href={link.href} 
                onClick={(e) => handleLinkClick(e, link.href)}
                className="text-[9px] font-bold uppercase tracking-wider text-slate-400 hover:text-foreground transition-colors"
              >
                {link.name}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <div className="hidden xl:flex items-center gap-4">
              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-full bg-foreground/5 hover:bg-white/10 border border-foreground/10 transition-all flex items-center justify-center text-slate-400 hover:text-foreground"
                title={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              
              <a 
                href="https://wa.me/5511949830659"
                
                rel="noopener noreferrer"
                className="w-fit px-6 py-2.5 bg-orange-600 hover:bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest rounded-full transition-all flex items-center gap-2 shadow-lg shadow-orange-600/20"
              >
                Solicitar um Orçamento
              </a>
            </div>

            {/* Mobile Menu Trigger */}
            <button 
              onClick={() => setIsMenuOpen(true)}
              className="xl:hidden p-3 bg-foreground/5 rounded-2xl border border-foreground/10 text-foreground"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="pt-0">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-background border-t border-foreground/5 py-12 md:py-20 relative overflow-hidden">
        {/* Animated Gradient Border Top */}
        <div className="absolute top-0 left-0 right-0 h-[1px] overflow-hidden">
          <motion.div 
            animate={{ x: ["-100%", "100%"] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            className="w-full h-full bg-gradient-to-r from-transparent via-orange-500 to-blue-500"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-12 md:mb-20 text-center sm:text-left">
            <div className="flex flex-col items-center sm:items-start">
              <Logo className="mb-4 md:mb-6 scale-90 origin-left" />
              <p className="text-slate-500 text-xs md:text-sm leading-relaxed mb-6 max-w-xs hidden md:block">
                Contabilidade estratégica que escala seus resultados. Tradição e Inteligência desde 1969.
              </p>
              <div className="flex items-center gap-4">
                <a href="https://www.instagram.com/jwcosta_contabilidade/" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 bg-foreground/5 rounded-xl flex items-center justify-center ${theme === 'dark' ? 'text-orange-500' : 'text-blue-500'} hover:bg-orange-600 hover:text-white transition-all`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
                </a>
                <a href="https://www.linkedin.com/in/jwcosta-solu%C3%A7%C3%B5es-cont%C3%A1beis-00369b110/" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 bg-foreground/5 rounded-xl flex items-center justify-center ${theme === 'dark' ? 'text-orange-500' : 'text-blue-500'} hover:bg-orange-600 hover:text-white transition-all`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></svg>
                </a>
                <a href="https://www.facebook.com/JwcostaC" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 bg-foreground/5 rounded-xl flex items-center justify-center ${theme === 'dark' ? 'text-orange-500' : 'text-blue-500'} hover:bg-orange-600 hover:text-white transition-all`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-1 md:col-span-1 lg:col-span-1 gap-8 md:gap-0">
              <div className="flex flex-col items-center sm:items-start">
                <h4 className="text-foreground font-black text-[9px] md:text-[10px] uppercase tracking-[0.2em] mb-4 md:mb-8">Especialidades</h4>
                <ul className="space-y-3 md:space-y-4">
                  <li><a href="#fiscal" onClick={(e) => handleLinkClick(e, '#fiscal')} className="text-slate-500 text-xs md:text-sm hover:text-orange-500 transition-colors font-medium">Escrita Fiscal</a></li>
                  <li><a href="#contabil" onClick={(e) => handleLinkClick(e, '#contabil')} className="text-slate-500 text-xs md:text-sm hover:text-orange-500 transition-colors font-medium">Contabilidade</a></li>
                  <li><a href="#esocial" onClick={(e) => handleLinkClick(e, '#esocial')} className="text-slate-500 text-xs md:text-sm hover:text-orange-500 transition-colors font-medium">Depto Pessoal</a></li>
                  <li><a href="#legal" onClick={(e) => handleLinkClick(e, '#legal')} className="text-slate-500 text-xs md:text-sm hover:text-orange-500 transition-colors font-medium">Legalização</a></li>
                </ul>
              </div>

              <div className="flex flex-col items-center sm:items-start lg:hidden mt-0">
                <h4 className="text-foreground font-black text-[9px] md:text-[10px] uppercase tracking-[0.2em] mb-4">Soluções</h4>
                <ul className="space-y-3">
                  <li><a href="#certificado" onClick={(e) => handleLinkClick(e, '#certificado')} className="text-slate-500 text-xs hover:text-orange-500 transition-colors font-medium">Certificado</a></li>
                  <li><a href="#seguros" onClick={(e) => handleLinkClick(e, '#seguros')} className="text-slate-500 text-xs hover:text-orange-500 transition-colors font-medium">Seguros</a></li>
                  <li><a href="#irpf" onClick={(e) => handleLinkClick(e, '#irpf')} className="text-slate-500 text-xs hover:text-orange-500 transition-colors font-medium">IRPF</a></li>
                </ul>
              </div>
            </div>

            <div className="hidden lg:flex flex-col items-center sm:items-start">
              <h4 className="text-foreground font-black text-[10px] uppercase tracking-[0.2em] mb-8">Soluções</h4>
              <ul className="space-y-4">
                <li><a href="#certificado" onClick={(e) => handleLinkClick(e, '#certificado')} className="text-slate-500 text-sm hover:text-orange-500 transition-colors font-medium">Certificado Digital</a></li>
                <li><a href="#seguros" onClick={(e) => handleLinkClick(e, '#seguros')} className="text-slate-500 text-sm hover:text-orange-500 transition-colors font-medium">Seguros e Saúde</a></li>
                <li><a href="#irpf" onClick={(e) => handleLinkClick(e, '#irpf')} className="text-slate-500 text-sm hover:text-orange-500 transition-colors font-medium">Consultoria IRPF</a></li>
                <li><a href="https://onvio.com.br/clientcenter/pt/auth?r=%2Fhome" target="_blank" rel="noopener noreferrer" className="text-slate-500 text-sm hover:text-orange-500 transition-colors font-medium">Portal do Cliente</a></li>
              </ul>
            </div>

            <div className="flex flex-col items-center sm:items-start">
              <h4 className="text-foreground font-black text-[9px] md:text-[10px] uppercase tracking-[0.2em] mb-6 md:mb-8">Contato</h4>
              <ul className="space-y-4 md:space-y-6">
                <li className="flex items-center gap-3 text-slate-500 justify-center sm:justify-start">
                  <Phone size={14} className="text-orange-500 shrink-0" />
                  <span className="text-xs md:text-sm font-medium">3751-4422</span>
                </li>
                <li className="flex items-center gap-3 text-slate-500 justify-center sm:justify-start">
                  <Mail size={14} className="text-orange-500 shrink-0" />
                  <span className="text-xs md:text-sm font-medium">contato@jwcosta.com.br</span>
                </li>
                <li className="flex items-start gap-3 text-slate-500 justify-center sm:justify-start">
                  <Globe size={14} className="text-orange-500 shrink-0 mt-0.5" />
                  <span className="text-[10px] md:text-xs leading-relaxed font-medium">Taboão da Serra / SP</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 md:pt-12 border-t border-foreground/5 flex flex-col md:flex-row justify-between items-center gap-6 md:gap-8 text-center md:text-left">
            <div className="space-y-2">
              <p className="text-slate-600 text-[8px] md:text-[10px] font-black uppercase tracking-widest">
                © 2024 JWCosta | CNPJ: 23.812.994/0001-46
              </p>
              <div className="text-slate-500 text-[8px] md:text-[10px] flex items-center justify-center md:justify-start gap-2">
                Desenvolvido com amor 
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="text-red-500"
                >
                  ❤️
                </motion.span>
                por Rodrigo da Costa
              </div>
            </div>
            
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="text-slate-500 hover:text-orange-500 text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] transition-all flex items-center gap-2 group"
            >
              Voltar ao Topo
              <ArrowUp size={12} className="group-hover:-translate-y-1 transition-transform" />
            </button>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={`relative w-full max-w-lg ${theme === 'dark' ? 'bg-white/[0.03] border-white/10' : 'bg-white border-slate-200'} backdrop-blur-2xl border rounded-[3rem] p-10 shadow-2xl`}
            >
              <button 
                onClick={closeModal}
                className="absolute top-6 right-6 text-slate-400 hover:text-foreground transition-colors"
              >
                <X size={24} />
              </button>

              <div className="mb-10">
                <h3 className="text-3xl font-black text-foreground tracking-tight mb-2">Solicitar Orçamento</h3>
                <p className={`${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'} text-sm font-medium`}>Preencha os dados abaixo e retornaremos em breve.</p>
              </div>

              <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase text-slate-500 tracking-widest ml-1">Nome Completo</label>
                    <input type="text" className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-orange-500 outline-none transition-all text-foreground" placeholder="Seu nome" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase text-slate-500 tracking-widest ml-1">Nome da Empresa</label>
                    <input type="text" className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-orange-500 outline-none transition-all text-foreground" placeholder="Sua empresa" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase text-slate-500 tracking-widest ml-1">E-mail</label>
                    <input type="email" className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-orange-500 outline-none transition-all text-foreground" placeholder="exemplo@email.com" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase text-slate-500 tracking-widest ml-1">WhatsApp</label>
                    <input type="tel" className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-orange-500 outline-none transition-all text-foreground" placeholder="(11) 99999-9999" />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase text-slate-500 tracking-widest ml-1">Serviço de Interesse</label>
                  <select className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-orange-500 outline-none transition-all appearance-none text-foreground cursor-pointer">
                    <option className="bg-background text-foreground">Contabilidade Mensal</option>
                    <option className="bg-background text-foreground">Abertura de Empresa</option>
                    <option className="bg-background text-foreground">Imposto de Renda (IRPF)</option>
                    <option className="bg-background text-foreground">eSocial Doméstico</option>
                    <option className="bg-background text-foreground">Certificado Digital</option>
                    <option className="bg-background text-foreground">Seguros</option>
                    <option className="bg-background text-foreground">Outros</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase text-slate-500 tracking-widest ml-1">Mensagem</label>
                  <textarea className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-orange-500 outline-none transition-all h-24 resize-none text-foreground" placeholder="Conte-nos mais sobre sua necessidade..."></textarea>
                </div>

                <button 
                  type="submit"
                  className="w-full bg-orange-600 hover:bg-orange-500 text-white font-black py-4 rounded-xl transition-all shadow-lg shadow-orange-600/20 flex items-center justify-center gap-2 uppercase tracking-widest text-[10px] mt-6"
                >
                  Enviar Solicitação
                  <Send size={14} />
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Bottom Sheet Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm xl:hidden"
            />
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className={`fixed bottom-0 left-0 right-0 z-[70] ${theme === 'dark' ? 'bg-slate-950/90' : 'bg-white/90'} backdrop-blur-2xl rounded-t-[2.5rem] border-t border-foreground/10 p-8 pt-4 xl:hidden max-h-[90vh] overflow-y-auto shadow-[0_-20px_40px_rgba(0,0,0,0.3)]`}
            >
              {/* Handle */}
              <div className="w-12 h-1.5 bg-foreground/10 rounded-full mx-auto mb-8" />
              
              <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between mb-2">
                  <Logo />
                  <button 
                    onClick={toggleTheme}
                    className="flex items-center gap-2 px-4 py-2 bg-foreground/5 rounded-full border border-foreground/10 text-xs font-bold text-slate-400"
                  >
                    {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
                    {theme === 'dark' ? 'Tema Claro' : 'Tema Escuro'}
                  </button>
                </div>

                <nav className="grid grid-cols-2 gap-3">
                  {menuLinks.map((link) => (
                    <a 
                      key={link.name} 
                      href={link.href}
                      onClick={(e) => handleLinkClick(e, link.href)}
                      className="px-5 py-4 bg-foreground/5 rounded-2xl border border-foreground/5 text-sm font-bold text-foreground hover:bg-orange-500/10 hover:text-orange-500 transition-all"
                    >
                      {link.name}
                    </a>
                  ))}
                </nav>

                <div className="pt-4 space-y-3">
                  <a 
                    href="https://onvio.com.br/clientcenter/pt/auth?r=%2Fhome"
                    className="w-full py-4 bg-orange-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 shadow-lg shadow-orange-600/20"
                  >
                    <User size={16} />
                    Área do Cliente
                  </a>
                  <button 
                    onClick={() => { setIsMenuOpen(false); toggleModal(); }}
                    className="w-full py-4 bg-foreground/10 text-foreground rounded-2xl font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 border border-foreground/10"
                  >
                    Solicitar Orçamento
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Back to Top */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-8 right-8 z-[55] w-14 h-14 bg-orange-600 text-white rounded-2xl flex items-center justify-center shadow-2xl shadow-orange-600/30 hover:scale-110 transition-all"
          >
            <ArrowUp size={24} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
